import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { modsRecordPath, MultiOfferModel } from 'client/data/models/multi-offer';
import {
  VehicleModel,
  VehicleEntities,
  buildEstimatedStyleIdsPath,
  buildVehicleStylePath,
} from 'client/data/models/vehicle-v2';
import { AppraisalModel, buildAllStylesAppraisalPath, AppraisalEntities } from 'client/data/models/appraisal';
import { LANDING_URL_PATHS } from 'site-modules/shared/constants/appraisal/paths';
import { getQuery } from 'client/utils/location';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { usePrivacyChoiceTracking } from 'site-modules/shared/hooks/use-privacy-choice-tracking';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import {
  APPRAISER_PRELOAD_EXPERIMENT,
  APPRAISER_PRELOAD_RECIPES,
  getAppraiserStylesPath,
  isAllStylesPreloadEligible,
  selectEmo4182Context,
} from 'client/data/utils/appraiser-preload-experiment';

import { AppraiserUI } from './appraiser';
import { AppraiserHighlightsUI } from './appraiser-highlights';
import { getSelectedStyleId, transformStylesToStyleIds } from './appraiser-utils';

export function AppraiserContainerUI({
  isMobile = false,
  estimatedVehicle = null,
  appraisalStyles = [],
  params,
  location = {},
  isLandingReferer = false,
  isAppExtensionPage = false,
  estimatedAppraisalVin = null,
  modsRecord = null,
  setModelValue,
  LocationAndStyleStepComponent,
  isEmo2933Chal1 = false,
  isHighlightsPage = false,
}) {
  usePrivacyChoiceTracking();

  const commonAppraiserProps = {
    isMobile,
    estimatedVehicle,
    appraisalStyles,
    params,
    location,
    isLandingReferer,
    estimatedAppraisalVin,
    modsRecord,
    setModelValue,
    LocationAndStyleStepComponent,
    isEmo2933Chal1,
  };
  const { styleIds } = getQuery(location);

  // Exposing all props so prop types are clearly observed and lint does not complain
  return isHighlightsPage ? (
    <AppraiserHighlightsUI {...commonAppraiserProps} />
  ) : (
    <Fragment>
      <AppraiserUI {...commonAppraiserProps} isAppExtensionPage={isAppExtensionPage} />
      {isAllStylesPreloadEligible(styleIds) && (
        <Experiment name={APPRAISER_PRELOAD_EXPERIMENT} showDefault>
          <Recipe name={APPRAISER_PRELOAD_RECIPES.CTRL} isDefault />
          <Recipe name={APPRAISER_PRELOAD_RECIPES.CHAL_1} />
        </Experiment>
      )}
      <Experiment name="EMO-3500" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal1" />
      </Experiment>
      <Experiment name="ads-12559" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal" />
      </Experiment>
    </Fragment>
  );
}

AppraiserContainerUI.propTypes = {
  isMobile: PropTypes.bool,
  estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
  appraisalStyles: AppraisalEntities.Styles,
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({}),
  isLandingReferer: PropTypes.bool,
  isAppExtensionPage: PropTypes.bool,
  estimatedAppraisalVin: PropTypes.string,
  modsRecord: PropTypes.shape({}),
  setModelValue: PropTypes.func.isRequired,
  LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  isEmo2933Chal1: PropTypes.bool,
  isHighlightsPage: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  isLandingReferer: LANDING_URL_PATHS.some(path => get(state, 'pageContext.page.referer', '').includes(path)),
  isAppExtensionPage: get(state, 'pageContext.isAppExtensionPage'),
  pageName: get(state, 'pageContext.page.name'),
  emo4182Recipe: selectEmo4182Context(state).emo4182Recipe,
});

export const stateToPropsConfig = {
  estimatedVehicle: bindToPath(({ estimatedStyleIds = {}, allStyleIdsForVehicle = [], location = {} }) => {
    const { styleIds } = getQuery(location);
    const selectedStyleId = getSelectedStyleId(estimatedStyleIds, styleIds, allStyleIdsForVehicle);
    return selectedStyleId ? buildVehicleStylePath({ styleId: selectedStyleId }) : '';
  }, VehicleModel),
  appraisalStyles: bindToPath(({ params }) => buildAllStylesAppraisalPath(params), AppraisalModel),
  estimatedAppraisalVin: bindToPath('estimatedAppraisalVin', VehicleModel),
  modsRecord: bindToPath(({ location }) => {
    const { modsId } = getQuery(location);
    return modsId ? modsRecordPath.build(modsId) : '';
  }, MultiOfferModel),
};

export const stateToPropsConfigPrefetch = {
  estimatedStyleIds: bindToPath(
    ({ params }) => buildEstimatedStyleIdsPath({ make: params.make, model: params.model, year: params.year }),
    VehicleModel
  ),
  allStyleIdsForVehicle: bindToPath(
    ({ params = {}, location = {}, pageName = '', emo4182Recipe = 'ctrl' }) =>
      getAppraiserStylesPath({
        pageName,
        location,
        emo4182Recipe,
        make: makeNiceName(params.make),
        model: makeNiceName(params.model),
        year: params.year,
      }),
    VehicleModel,
    transformStylesToStyleIds
  ),
};

export const AppraiserContainer = connect(mapStateToProps)(connectToModel(AppraiserContainerUI, stateToPropsConfig));

AppraiserContainer.propTypes = {
  estimatedStyleIds: VehicleEntities.VehicleStyleIds,
  allStyleIdsForVehicle: PropTypes.arrayOf(PropTypes.string),
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({}),
  LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  isHighlightsPage: PropTypes.bool,
};

const mapStateToPropsPrefetch = state => {
  const { pageName, emo4182Recipe } = selectEmo4182Context(state);
  return { pageName, emo4182Recipe };
};

const AppraiserContainerPrefetchModel = connect(mapStateToPropsPrefetch)(
  connectToModel(AppraiserContainer, stateToPropsConfigPrefetch)
);

export const AppraiserContainerPrefetch = props => <AppraiserContainerPrefetchModel {...props} />;
